import { Link } from "react-router-dom";
import { User, Image, Activity, CreditCard, Bug, Fingerprint, Lock, Trash2, EyeOff, MapPin, Check } from "lucide-react";

const COLLECTED = [
  { Icon: User, cat: "Personal info", data: "Name, email address, user ID (from Google Sign-In, or Sign in with Apple on iOS)", why: "Account management" },
  { Icon: Image, cat: "Photos", data: "Profile photo — optional, only if you add one", why: "App functionality" },
  { Icon: Activity, cat: "App activity", data: "Challenge attempts, scores, rankings, badges, follows and in-app interactions", why: "App functionality, leaderboards" },
  { Icon: CreditCard, cat: "Purchases", data: "Purchase history — plan, amount and transaction ID from our secure payment partners. Card, UPI and payment account details are never received or stored by us", why: "App functionality, invoices" },
  { Icon: Bug, cat: "App info & performance", data: "Crash logs and diagnostics", why: "Analytics, app stability" },
  { Icon: Fingerprint, cat: "Device or other IDs", data: "Device identifiers and approximate region from IP address", why: "Fraud prevention, security" },
];

const NOT_COLLECTED = ["Precise location", "Contacts", "Messages or calls", "Health & fitness", "Financial account details", "Browsing history", "Audio or files"];

const PRACTICES = [
  { Icon: Lock, t: "Encrypted in transit and at rest" },
  { Icon: Trash2, t: "You can request that data be deleted" },
  { Icon: EyeOff, t: "Not sold, no ads, no third-party tracking" },
  { Icon: MapPin, t: "Stored on servers in India" },
];

export const DataSafety = () => (
  <div className="glass rounded-3xl p-6 sm:p-8" data-testid="data-safety">
    <p className="eyebrow mb-2">Data safety at a glance</p>
    <h2 className="font-display font-bold text-base md:text-lg text-[var(--ink)]">What the TruCitizen app collects — and what it never does</h2>
    <p className="mt-2 text-sm text-[var(--muted)] leading-relaxed">Mirrors our Google Play Data Safety and App Store App Privacy labels. All data listed here is linked to your account and used only for the purposes shown.</p>

    <ul className="mt-6 divide-y divide-black/[0.08]" data-testid="data-safety-collected">
      {COLLECTED.map(({ Icon, cat, data, why }) => (
        <li key={cat} className="py-4 flex gap-4" data-testid={`data-safety-row-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <span className="w-9 h-9 shrink-0 rounded-full bg-[rgba(232,106,34,0.12)] text-[var(--saffron-deep)] flex items-center justify-center"><Icon size={16} /></span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <p className="font-bold text-[var(--ink)]">{cat}</p>
              <p className="font-mono text-[0.68rem] tracking-[0.12em] uppercase text-[var(--green-deep)]">{why}</p>
            </div>
            <p className="mt-1 text-sm text-[var(--ink-soft)] leading-relaxed">{data}</p>
          </div>
        </li>
      ))}
    </ul>

    <div className="mt-6 pt-6 border-t border-black/[0.08]">
      <p className="eyebrow mb-3">Not collected</p>
      <div className="flex flex-wrap gap-2" data-testid="data-safety-not-collected">
        {NOT_COLLECTED.map((n) => (
          <span key={n} className="px-3 py-1 rounded-full text-xs font-semibold bg-white/70 border border-black/10 text-[var(--ink-soft)] line-through decoration-[var(--muted)]">{n}</span>
        ))}
      </div>
    </div>

    <div className="mt-6 pt-6 border-t border-black/[0.08]">
      <p className="eyebrow mb-3">Security practices</p>
      <ul className="grid sm:grid-cols-2 gap-3" data-testid="data-safety-practices">
        {PRACTICES.map(({ Icon, t }) => (
          <li key={t} className="flex items-center gap-3 text-sm font-semibold text-[var(--ink)]">
            <span className="w-7 h-7 shrink-0 rounded-full bg-[rgba(35,133,79,0.12)] text-[var(--green-deep)] flex items-center justify-center"><Check size={14} /></span>
            <Icon size={16} className="text-[var(--muted)] shrink-0" /> {t}
          </li>
        ))}
      </ul>
    </div>

    <p className="mt-6 text-xs text-[var(--muted)] leading-relaxed">
      Summary only — the full details, your rights and our Data Processors are in the <Link to="/privacy-policy" className="underline underline-offset-2 text-[var(--saffron-deep)]" data-testid="data-safety-privacy-link">Privacy Policy</Link>.
    </p>
  </div>
);
